/**
 * The record browser inside Admin → Directories — look at what a feed actually imported.
 *
 * Until now the only way to see an imported record was to guess its number or name in the intake
 * form's picker. That is fine for using the data and useless for checking it — after an import of
 * eighteen thousand clients the questions are "did the names come through whole", "how many are
 * closed", and "is this one in here", none of which a type-ahead answers.
 *
 * Read-only, deliberately. These records are a copy of a source system; every change arrives with
 * the next export, and `clients` / `directories/**` are `allow write: if false` for every client
 * including administrators. Removing an import is the one exception and lives on the Feeds
 * sub-tab, beside the import that produced it.
 *
 * Paging is cursor-based rather than offset-based because Firestore has no offset worth using:
 * `startAfter(lastDoc)` is the only way to page a large collection without re-reading everything
 * before it. The status filter uses the composite indexes declared in firestore.indexes.json.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useDoc } from '../../lib/useFirestore';
import { allFeeds, feedRecordsPath } from '../../lib/feedRegistry';
import { useClientSearch } from '../../lib/useClientSearch';

const PAGE_SIZE = 50;

const STATUS_FILTERS = [
  ['all', 'All records'],
  ['active', 'Active only'],
  ['inactive', 'Inactive only'],
];

const input =
  'rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs';

/** Total, active and inactive, from the server rather than by counting downloaded documents. */
function useDirectoryCounts(feedId) {
  const [counts, setCounts] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const records = collection(db, ...feedRecordsPath(feedId));
    Promise.all([
      getCountFromServer(records),
      getCountFromServer(query(records, where('isActive', '==', true))),
    ])
      .then(([total, active]) => {
        if (cancelled) return;
        const all = total.data().count;
        const live = active.data().count;
        setCounts({ total: all, active: live, inactive: all - live });
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [feedId]);

  return { counts, error };
}

export default function DirectoryRecords({ onConfigure }) {
  const { data: feedsDoc, loading } = useDoc('settings/feeds');
  const [feedId, setFeedId] = useState('clients');
  const [statusFilter, setStatusFilter] = useState('all');
  const [term, setTerm] = useState('');
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const feeds = useMemo(() => allFeeds(feedsDoc?.feeds).filter((feed) => feed.columnMapped), [feedsDoc]);
  const settings = feedsDoc?.feeds?.[feedId];
  const { counts, error: countError } = useDirectoryCounts(feedId);

  // Which imported columns to show, in the order the administrator mapped them. Everything the
  // feed keeps is here — including columns that fill in no form field, since checking an import
  // means looking at what landed, not at what the form uses.
  const columns = useMemo(() => {
    const mapped = Array.isArray(settings?.columns) ? settings.columns : [];
    return mapped.filter((column) => column.key && column.role !== 'ignore' && column.role !== 'number' && column.role !== 'name');
  }, [settings]);

  const loadPage = useCallback(
    async (after) => {
      setBusy(true);
      setError('');
      try {
        const clauses = [];
        if (statusFilter === 'active') clauses.push(where('isActive', '==', true));
        if (statusFilter === 'inactive') clauses.push(where('isActive', '==', false));
        clauses.push(orderBy('clientNameLower'));
        if (after) clauses.push(startAfter(after));
        clauses.push(limit(PAGE_SIZE));
        const snap = await getDocs(query(collection(db, ...feedRecordsPath(feedId)), ...clauses));
        const page = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setRows((prev) => (after ? [...prev, ...page] : page));
        setCursor(snap.docs[snap.docs.length - 1] || null);
        setDone(page.length < PAGE_SIZE);
      } catch (err) {
        setError(err.message || String(err));
      } finally {
        setBusy(false);
      }
    },
    [feedId, statusFilter]
  );

  // Reset and reload whenever the directory or the filter changes.
  useEffect(() => {
    setRows([]);
    setCursor(null);
    setDone(false);
    loadPage(null);
  }, [loadPage]);

  // A search asks a different question than a browse, so it replaces the list rather than
  // filtering it. Inactive records are included and labelled here even when the picker hides
  // them: the point of this page is seeing everything that was imported.
  const search = useClientSearch(term, { max: 25, includeInactive: true, feedId });
  const searching = term.trim().length >= 2;
  const visible = searching
    ? search.results.filter((row) =>
        statusFilter === 'active'
          ? row.isActive !== false
          : statusFilter === 'inactive'
            ? row.isActive === false
            : true
      )
    : rows;

  if (loading) return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <label className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
          Directory
          <select className={input} value={feedId} onChange={(e) => setFeedId(e.target.value)}>
            {feeds.map((feed) => (
              <option key={feed.id} value={feed.id}>
                {feed.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
          Show
          <select className={input} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {STATUS_FILTERS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <input
          className={`${input} w-64`}
          placeholder="Search by number or name…"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
        />
      </div>

      <p className="text-xs text-slate-600 dark:text-slate-300">
        {countError ? (
          <span className="text-red-600 dark:text-red-400">This directory could not be counted ({countError})</span>
        ) : counts ? (
          <>
            <strong>{counts.total.toLocaleString()}</strong> records —{' '}
            {counts.active.toLocaleString()} active, {counts.inactive.toLocaleString()} inactive.{' '}
            {counts.total === 0 && (
              <>
                Nothing has been imported into this directory yet.{' '}
                {/* The next thing to do is on the other tab, so say so and go there rather than
                    leaving an empty table as the whole answer. */}
                {onConfigure && (
                  <button
                    type="button"
                    className="font-medium text-firm-700 dark:text-firm-300 hover:underline"
                    onClick={onConfigure}
                  >
                    Set up its feed
                  </button>
                )}
              </>
            )}
          </>
        ) : (
          'Counting…'
        )}
      </p>

      {/* Says plainly that this is a copy, so nobody looks for an edit button that should not
          exist: a directory the app can be talked into editing is no longer a faithful copy of
          the source system. */}
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Read-only — these records come from the export and change with the next one. To remove an
        import,{' '}
        {onConfigure ? (
          <button
            type="button"
            className="font-medium text-firm-700 dark:text-firm-300 hover:underline"
            onClick={onConfigure}
          >
            go to Feeds
          </button>
        ) : (
          <strong>Feeds</strong>
        )}
        .
      </p>

      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}

      <div className="overflow-x-auto rounded border border-slate-200 dark:border-slate-700">
        <table className="w-full min-w-[720px] text-xs">
          <thead className="bg-slate-50 dark:bg-slate-800 text-left text-slate-500 dark:text-slate-400">
            <tr>
              <th className="px-2 py-1">Number</th>
              <th className="px-2 py-1">Name</th>
              <th className="px-2 py-1">Status</th>
              {columns.map((column) => (
                <th key={column.key} className="px-2 py-1">
                  {column.label || column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.id} className="border-t border-slate-100 dark:border-slate-800">
                <td className="px-2 py-1 font-medium text-slate-700 dark:text-slate-200">{row.clientNumber}</td>
                <td className="px-2 py-1">{String(row.clientName || '').toUpperCase()}</td>
                <td className="px-2 py-1">
                  {row.isActive === false ? (
                    <span className="rounded bg-amber-100 dark:bg-amber-900 px-1 text-amber-900 dark:text-amber-100">
                      {row.statusValue || 'inactive'}
                    </span>
                  ) : (
                    <span className="text-slate-500 dark:text-slate-400">{row.statusValue || 'active'}</span>
                  )}
                </td>
                {columns.map((column) => (
                  <td key={column.key} className="px-2 py-1 text-slate-600 dark:text-slate-300">
                    {(row.data && row.data[column.key]) || ''}
                  </td>
                ))}
              </tr>
            ))}
            {!visible.length && (
              <tr>
                <td colSpan={3 + columns.length} className="px-2 py-3 text-slate-500 dark:text-slate-400">
                  {busy || search.searching
                    ? 'Loading…'
                    : searching
                      ? 'No match. Search matches from the start of the number or name.'
                      : 'No records to show.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!searching && !done && (
        <button
          type="button"
          disabled={busy}
          className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1 text-xs text-slate-700 dark:text-slate-200 hover:border-firm-500 disabled:opacity-50"
          onClick={() => loadPage(cursor)}
        >
          {busy ? 'Loading…' : `Show ${PAGE_SIZE} more`}
        </button>
      )}
      {!searching && visible.length > 0 && (
        <p className="text-xs text-slate-400 dark:text-slate-500">
          Showing {visible.length.toLocaleString()}
          {done ? ' — that is all of them' : ''}
        </p>
      )}
    </div>
  );
}
